import * as THREE from "three";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import { resolveWallHeightM } from "../../domain/geometry/buildingDimensions";
import { ENGINE_WALL_PICK_PLANE_DEPTH_M } from "../../engines/constants";
import type { BuildInput } from "../../engines/types";
import type { SceneVisibility } from './sceneVisibility';
import {
	createOpeningMeasurementTarget,
	createPartitionMeasurementTarget,
	createWallMeasurementTarget,
	getWallLengthMm,
	getWallOverlayOpacities,
} from "./ViewerMeasurementController";

type InteractionKind = "opening" | "partition";

/** In-plane inset of an opening's pick box from the opening's own edges. */
const OPENING_HANDLE_INSET_M = 0.001;
/**
 * Through-wall depth of an opening's pick box, centred 90 mm in. Not a round
 * 80 mm: its faces then landed exactly on the procedural door handle's base
 * and, a few mm deeper, on the two-room door leaf's inner face.
 */
const OPENING_HANDLE_DEPTH_M = 0.073;

function createMeasurementMaterial(color: number, opacity: number) {
	return new THREE.MeshBasicMaterial({
		color,
		transparent: true,
		opacity,
		depthWrite: false,
		side: THREE.DoubleSide,
	});
}

/**
 * One wall-picking mesh with the openings removed from its triangles.
 *
 * A full facade plane used to continue straight through every door/window.
 * The opening box was slightly closer to the camera, but both transparent
 * surfaces still rendered over the physical door and competed at their
 * boundary whenever hover opacity changed. Subdividing on opening edges keeps
 * one wall target/material while making the raycast and the tint describe the
 * same visible wall surface.
 */
export function createOpeningAwareWallGeometry(
	wallLength: number,
	wallHeight: number,
	openings: WallOpening[],
): THREE.BufferGeometry {
	const xBreaks = new Set([0, wallLength]);
	const yBreaks = new Set([0, wallHeight]);
	const clipped = openings
		.map((opening) => ({
			x0: Math.max(0, opening.xMm / 1000),
			x1: Math.min(wallLength, (opening.xMm + opening.widthMm) / 1000),
			y0: Math.max(0, opening.yMm / 1000),
			y1: Math.min(wallHeight, (opening.yMm + opening.heightMm) / 1000),
		}))
		.filter((opening) => opening.x1 > opening.x0 && opening.y1 > opening.y0);

	for (const opening of clipped) {
		xBreaks.add(opening.x0);
		xBreaks.add(opening.x1);
		yBreaks.add(opening.y0);
		yBreaks.add(opening.y1);
	}

	const xs = [...xBreaks].sort((a, b) => a - b);
	const ys = [...yBreaks].sort((a, b) => a - b);
	const positions: number[] = [];
	const normals: number[] = [];
	const uvs: number[] = [];
	const indices: number[] = [];

	for (let xi = 0; xi < xs.length - 1; xi += 1) {
		for (let yi = 0; yi < ys.length - 1; yi += 1) {
			const [x0, x1] = [xs[xi], xs[xi + 1]];
			const [y0, y1] = [ys[yi], ys[yi + 1]];
			const centerX = (x0 + x1) / 2;
			const centerY = (y0 + y1) / 2;
			if (
				clipped.some(
					(opening) =>
						centerX > opening.x0 &&
						centerX < opening.x1 &&
						centerY > opening.y0 &&
						centerY < opening.y1,
				)
			) {
				continue;
			}

			const base = positions.length / 3;
			const localX0 = x0 - wallLength / 2;
			const localX1 = x1 - wallLength / 2;
			const localY0 = y0 - wallHeight / 2;
			const localY1 = y1 - wallHeight / 2;
			positions.push(
				localX0,
				localY0,
				0,
				localX1,
				localY0,
				0,
				localX1,
				localY1,
				0,
				localX0,
				localY1,
				0,
			);
			normals.push(0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1);
			uvs.push(
				x0 / wallLength,
				y0 / wallHeight,
				x1 / wallLength,
				y0 / wallHeight,
				x1 / wallLength,
				y1 / wallHeight,
				x0 / wallLength,
				y1 / wallHeight,
			);
			indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
		}
	}

	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute(
		"position",
		new THREE.Float32BufferAttribute(positions, 3),
	);
	geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
	geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
	geometry.setIndex(indices);
	geometry.computeBoundingSphere();
	return geometry;
}

export function createInteractionOverlays(buildInput: BuildInput): THREE.Group {
	const group = new THREE.Group();
	group.name = "detail/interaction/root";

	const { dimensions, openings, partitionSplit = 0.5 } = buildInput;
	const hasPartition = buildInput.hasRelaxRoom !== false;
	const wallBaseY = 0.165;
	const W = dimensions.width;
	const L = dimensions.length;
	const dividerZ = -L / 2 + L * partitionSplit;
	const wallHeight = resolveWallHeightM(buildInput.wallHeightM);
	const wallCenterY = wallBaseY + wallHeight / 2;
	// The wall pick/tint plane lives in the gap between the batts and the
	// studs' inner face. It used to sit 94 mm in, exactly on the batts' inner
	// face, and z-fought into a checkerboard whenever a wall was selected with
	// the panel hidden.
	const pickInset = ENGINE_WALL_PICK_PLANE_DEPTH_M;
	const openingsForWall = (wall: WallOpening["wall"]) =>
		openings.filter((opening) => opening.wall === wall);

	const addWallHandle = (
		wall: WallOpening["wall"],
		geometry: THREE.BufferGeometry,
		position: THREE.Vector3,
		rotation?: THREE.Euler,
	) => {
		const target = createWallMeasurementTarget(
			wall,
			getWallLengthMm(wall, buildInput),
		);
		const mesh = new THREE.Mesh(
			geometry,
			createMeasurementMaterial(0xf5efe6, 0),
		);
		const opacities = getWallOverlayOpacities(target.kind);
		mesh.position.copy(position);
		if (rotation) {
			mesh.rotation.copy(rotation);
		}
		mesh.name = `detail/interaction/wall/${wall}`;
		mesh.userData.measurementTarget = target;
		Object.assign(mesh.userData, opacities);
		group.add(mesh);
	};

	const addOpeningHandle = (
		opening: WallOpening,
		size: { x: number; y: number; z: number },
		position: THREE.Vector3,
		rotation?: THREE.Euler,
	) => {
		const target = createOpeningMeasurementTarget(
			opening,
			getWallLengthMm(opening.wall, buildInput),
		);
		const color = opening.type === "door" ? 0xf59e0b : 0x38bdf8;
		const opacities = getWallOverlayOpacities(target.kind);
		const mesh = new THREE.Mesh(
			new THREE.BoxGeometry(size.x, size.y, size.z),
			createMeasurementMaterial(color, opacities.baseOpacity),
		);
		mesh.position.copy(position);
		if (rotation) {
			mesh.rotation.copy(rotation);
		}
		mesh.name = `detail/interaction/opening/${opening.id}`;
		mesh.userData.interactionKind = "opening" satisfies InteractionKind;
		mesh.userData.openingId = opening.id;
		mesh.userData.cursor = "grab";
		mesh.userData.measurementTarget = target;
		Object.assign(mesh.userData, opacities);
		group.add(mesh);
	};

	addWallHandle(
		"front",
		createOpeningAwareWallGeometry(W, wallHeight, openingsForWall("front")),
		new THREE.Vector3(0, wallCenterY, L / 2 - pickInset),
	);
	addWallHandle(
		"back",
		createOpeningAwareWallGeometry(W, wallHeight, openingsForWall("back")),
		new THREE.Vector3(0, wallCenterY, -L / 2 + pickInset),
		new THREE.Euler(0, Math.PI, 0),
	);
	addWallHandle(
		"left",
		createOpeningAwareWallGeometry(L, wallHeight, openingsForWall("left")),
		new THREE.Vector3(-W / 2 + pickInset, wallCenterY, 0),
		new THREE.Euler(0, Math.PI / 2, 0),
	);
	addWallHandle(
		"right",
		createOpeningAwareWallGeometry(L, wallHeight, openingsForWall("right")),
		new THREE.Vector3(W / 2 - pickInset, wallCenterY, 0),
		new THREE.Euler(0, -Math.PI / 2, 0),
	);

	for (const opening of openings) {
		const widthM = opening.widthMm / 1000;
		const heightM = opening.heightMm / 1000;
		const yM = wallBaseY + opening.yMm / 1000 + heightM / 2;
		// The handle's sides would otherwise lie exactly on the reveal (panel
		// and glazing returns, header, sill, jambs) and flicker against them
		// on hover.
		const handleW = widthM - 2 * OPENING_HANDLE_INSET_M;
		const handleH = heightM - 2 * OPENING_HANDLE_INSET_M;

		if (opening.wall === "front" || opening.wall === "back") {
			const xM = -W / 2 + opening.xMm / 1000 + widthM / 2;
			addOpeningHandle(
				opening,
				{ x: handleW, y: handleH, z: OPENING_HANDLE_DEPTH_M },
				new THREE.Vector3(
					xM,
					yM,
					opening.wall === "front" ? L / 2 - 0.09 : -L / 2 + 0.09,
				),
			);
			continue;
		}

		if (opening.wall === "left" || opening.wall === "right") {
			const zM = -L / 2 + opening.xMm / 1000 + widthM / 2;
			addOpeningHandle(
				opening,
				{ x: OPENING_HANDLE_DEPTH_M, y: handleH, z: handleW },
				new THREE.Vector3(
					opening.wall === "left" ? -W / 2 + 0.09 : W / 2 - 0.09,
					yM,
					zM,
				),
			);
			continue;
		}

		if (opening.wall === "divider" && hasPartition) {
			const xM = -W / 2 + opening.xMm / 1000 + widthM / 2;
			addOpeningHandle(
				opening,
				{ x: handleW, y: handleH, z: OPENING_HANDLE_DEPTH_M },
				new THREE.Vector3(xM, yM, dividerZ - 0.02),
			);
		}
	}

	if (typeof buildInput.partitionSplit === "number" && hasPartition) {
		const target = createPartitionMeasurementTarget(buildInput);
		const partitionHandle = new THREE.Mesh(
			// 1 mm short of the side walls' stud faces and 70 mm deep, so its ends
			// and faces clear the studs, the folie and the divider door leaf
			// (a 60 mm bar put its face on the leaf, always faintly visible).
			new THREE.BoxGeometry(Math.max(0.6, W - 0.192), 0.06, 0.07),
			createMeasurementMaterial(0xffffff, 0.08),
		);
		const opacities = getWallOverlayOpacities(target.kind);
		partitionHandle.position.set(0, wallBaseY + 1.15, dividerZ);
		partitionHandle.name = "detail/interaction/partition";
		partitionHandle.userData.interactionKind =
			"partition" satisfies InteractionKind;
		partitionHandle.userData.cursor = "ns-resize";
		partitionHandle.userData.measurementTarget = target;
		Object.assign(partitionHandle.userData, opacities);
		group.add(partitionHandle);

		addWallHandle(
			"divider",
			createOpeningAwareWallGeometry(
				Math.max(0.4, W - 0.19),
				wallHeight,
				openingsForWall("divider").map((opening) => ({
					...opening,
					// Divider surface is inset 95mm at each side while opening x is
					// measured from the building's full-width origin.
					xMm: opening.xMm - 95,
				})),
			),
			// On the divider's stud axis: every divider layer and the door leaf
			// are boxes on either side of it, so none has a face here.
			new THREE.Vector3(0, wallCenterY, dividerZ),
		);
	}

	return group;
}

export function applyInteractionVisibility(
	group: THREE.Group | null,
	visibility: SceneVisibility,
): void {
	group?.traverse((object) => {
		const target = object.userData.measurementTarget;
		if (!target) return;
		const shellVisible =
			visibility.framing || visibility.panel || visibility.exterior;
		object.visible =
			shellVisible && (target.wall !== "front" || visibility.frontWall)
			&& (!(target.wall === "divider" || target.kind === "partition") || visibility.dividerWall !== false);
	});
}
